/**
 * Lightweight performance measurement utility for tracking operation durations.
 * Always collects timing data and returns it from `end()`.
 * Logs to the console only when `window.__NOVO_PERF_LOG__` is truthy.
 *
 * Usage:
 *   const perf = startMeasure("selectAll");
 *   // ... do work ...
 *   perf.mark("fetchComplete", { objectCount: 50000 });
 *   // ... more work ...
 *   perf.end({ totalIds: 120000 });
 */

export interface PerfMeasurement {
    /** Record an intermediate milestone with optional metadata. */
    mark(label: string, meta?: Record<string, unknown>): void;
    /** End the measurement. Logs total duration and all marks. */
    end(meta?: Record<string, unknown>): PerfResult;
}

export interface PerfResult {
    name: string;
    totalMs: number;
    marks: { label: string; elapsedMs: number; meta?: Record<string, unknown> }[];
    meta?: Record<string, unknown>;
}

/**
 * Observes the lifecycle of every measurement so the telemetry layer can map
 * measurements onto OpenTelemetry spans.
 *
 * `onStart` runs when `startMeasure()` is called and may return an opaque
 * handle (e.g. the underlying span) that is handed back to `onEnd`. The
 * `parent` argument is the handle of the explicitly-provided parent
 * measurement (or `undefined`), letting nested measurements be linked as
 * parent/child.
 */
export interface MeasurementSink {
    onStart(name: string, parent: unknown): unknown;
    onEnd(handle: unknown, result: PerfResult): void;
}

let measurementSink: MeasurementSink | undefined;

/**
 * Maps each measurement to the opaque handle returned by the sink's `onStart`
 * (e.g. its OpenTelemetry span), so an explicit `parent` measurement can be
 * resolved to its handle when starting a child. A WeakMap keeps the public
 * `PerfMeasurement` type clean and lets entries be collected with the
 * measurement.
 */
const measurementHandles = new WeakMap<PerfMeasurement, unknown>();

/**
 * Register a sink notified when measurements start and end.
 *
 * This indirection keeps this dependency-free utility decoupled from the
 * telemetry module: the telemetry layer registers a sink that maps
 * measurements to OpenTelemetry spans, without `performanceMeasure` needing to
 * import telemetry (which would create a circular dependency).
 *
 * Pass `undefined` to clear the sink (e.g. in test teardown).
 */
export function setMeasurementSink(sink: MeasurementSink | undefined): void {
    measurementSink = sink;
}

function isPerfLoggingEnabled(): boolean {
    return typeof window !== "undefined" && !!(window as unknown as Record<string, unknown>).__NOVO_PERF_LOG__;
}

/**
 * Start a named performance measurement.
 *
 * Pass `parent` to nest this measurement under another one (producing a
 * parent/child span in telemetry). Parenting is explicit by design: there is
 * no ambient "current measurement", so overlapping async operations are never
 * mis-parented. A child should be started while its parent is still open.
 *
 * Always collects data; only logs to console when `window.__NOVO_PERF_LOG__ = true`.
 * Set this flag at any time in the browser console before triggering the operation.
 */
export function startMeasure(name: string, parent?: PerfMeasurement): PerfMeasurement {
    const now =
        typeof performance !== "undefined" && typeof performance.now === "function"
            ? () => performance.now()
            : () => Date.now();
    const t0 = now();
    const marks: PerfResult["marks"] = [];

    const parentHandle = parent ? measurementHandles.get(parent) : undefined;
    let handle: unknown;
    if (measurementSink) {
        try {
            handle = measurementSink.onStart(name, parentHandle);
        } catch {
            // Telemetry must never break the measured operation.
        }
    }

    let ended = false;

    const measurement: PerfMeasurement = {
        mark(label: string, meta?: Record<string, unknown>) {
            if (ended) {
                return;
            }
            marks.push({ label, elapsedMs: now() - t0, meta });
        },
        end(meta?: Record<string, unknown>): PerfResult {
            const totalMs = now() - t0;
            const result: PerfResult = { name, totalMs, marks, meta };

            if (!ended) {
                ended = true;
                measurementHandles.delete(measurement);
                if (measurementSink) {
                    try {
                        measurementSink.onEnd(handle, result);
                    } catch {
                        // Telemetry must never break the measured operation.
                    }
                }
            }

            if (isPerfLoggingEnabled()) {
                // eslint-disable-next-line no-console
                console.groupCollapsed(`[perf] ${name}: ${totalMs.toFixed(1)}ms`);
                for (const m of marks) {
                    // eslint-disable-next-line no-console
                    console.log(`  ${m.label}: ${m.elapsedMs.toFixed(1)}ms`, m.meta ?? "");
                }
                if (meta) {
                    // eslint-disable-next-line no-console
                    console.log("  result:", meta);
                }
                // eslint-disable-next-line no-console
                console.groupEnd();
            }

            return result;
        },
    };

    if (handle !== undefined) {
        measurementHandles.set(measurement, handle);
    }

    return measurement;
}
